import React, { useState } from "react";
import { User } from "../types";
import { Logo } from "../components/Logo";
import { supabase } from "../services/supabaseClient";

interface LoginProps {
  onLogin: (user: User) => void;
  onNavigate: (page: string) => void;
}

type DbUserRow = {
  id: string;
  email: string | null;
  name: string | null;
  xp: number | null;
  level: number | null;
  role: string | null;
  subscription_status: string | null;
  subscription_plan: string | null;
  subscription_end_date: string | null;
  created_at: string | null;
};

function mapDbUserToAppUser(row: DbUserRow): User {
  return {
    id: row.id,
    email: row.email ?? "",
    name: row.name ?? "",
    xp: row.xp ?? 0,
    level: row.level ?? 1,
    role: (row.role ?? "user") as any,
    subscriptionStatus: (row.subscription_status ?? "free") as any,
    subscriptionPlan: (row.subscription_plan ?? undefined) as any,
    subscriptionEndDate: row.subscription_end_date ?? undefined,
    createdAt: row.created_at ?? undefined,
  };
}

export const Login: React.FC<LoginProps> = ({ onLogin, onNavigate }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchProfile = async (userId: string): Promise<User> => {
    const { data, error } = await supabase.from("users").select("*").eq("id", userId).single();
    if (error) throw error;
    return mapDbUserToAppUser(data as DbUserRow);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setErrorMsg("");

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      }); // login por email/senha [web:1418]

      if (error) {
        setErrorMsg(error.message || "E-mail ou senha inválidos.");
        return;
      }

      const userId = data.user?.id;
      if (!userId) {
        // fallback: pega usuário da sessão atual (caso data.user venha vazio por algum motivo) [web:1154]
        const { data: userData, error: userErr } = await supabase.auth.getUser(); // [web:1154]
        if (userErr || !userData.user?.id) {
          setErrorMsg("Login realizado, mas não foi possível identificar o usuário.");
          return;
        }
        const profile = await fetchProfile(userData.user.id);
        onLogin(profile);
        return;
      }

      const profile = await fetchProfile(userId);
      onLogin(profile);
    } catch (err: any) {
      setErrorMsg(err?.message || "Erro inesperado no login. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1630] flex flex-col">
      <header className="w-full flex justify-center pt-10 pb-6">
        <div className="flex flex-col items-center gap-1 group cursor-pointer" onClick={() => onNavigate("landing")}>
          <Logo size="md" className="group-hover:scale-110 transition-transform duration-500" />
          <div className="text-center">
            <h2 className="text-xl font-black text-white tracking-tight leading-none uppercase">UroInsights</h2>
            <p className="text-[10px] font-black text-blue-300 uppercase tracking-[0.4em] mt-1">Academy</p>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white p-12 rounded-[3rem] shadow-2xl border border-slate-100">
          <div className="mb-10 text-center">
            <h3 className="text-4xl font-black text-slate-900 tracking-tighter mb-2">Login Academy</h3>
            <p className="text-slate-400 font-bold text-sm">Acesse sua jornada urológica.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {errorMsg && (
              <div className="p-4 bg-red-50 text-red-700 text-sm font-black rounded-2xl border border-red-100">
                {errorMsg}
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-5 bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-3xl transition-all font-bold text-slate-800 outline-none shadow-sm"
                placeholder="seuemail@exemplo.com"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                Senha
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-5 bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-3xl transition-all font-bold text-slate-800 outline-none shadow-sm"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-6 mt-2 bg-blue-600 text-white rounded-[2rem] font-black text-xl shadow-2xl shadow-blue-100 hover:bg-blue-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? "Entrando..." : "Entrar na Plataforma"}
            </button>
          </form>

          <div className="mt-10 text-center pt-8 border-t border-slate-50">
            <p className="text-slate-400 font-bold text-sm">
              Não tem conta?{" "}
              <button
                type="button"
                onClick={() => onNavigate("signup")}
                className="text-blue-600 font-black hover:underline ml-1"
              >
                Criar conta
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
